/* Serie diaria: linha para o acumulado, colunas para o que entrou no dia.
 *
 * Desenhado em pixel, e nao num viewBox escalado como a Sparkline: aqui o grafico
 * ocupa meia pagina e tem eixo, e um viewBox esticado engorda o texto junto com a
 * largura. A largura vem de um ResizeObserver; o resto e a mesma regra da casa —
 * toda cor e `var(--...)`, entao o tema recolore sem re-render.
 *
 * O hover acha o DIA, nao a marca: a coluna inteira do dia e alvo, e as setas do
 * teclado andam de dia em dia com o grafico em foco. O mesmo valor esta na tabela
 * do ChartFrame, entao o tooltip nunca e o unico caminho ate o numero. */

import { useLayoutEffect, useRef, useState } from "react";
import { compact, intBR, MESES } from "../lib/format";

const H = 220, PT = 18, PB = 24, PR = 14;
const DIAS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

function parts(day: string) {
  const [y, m, d] = day.split("-").map(Number);
  return { y, m, d, weekday: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
}

/** `2026-05-15` -> `15 mai`. */
export function fmtDia(day: string): string {
  const { m, d } = parts(day);
  return `${d} ${MESES[m - 1]}`;
}

/** `2026-05-15` -> `sex, 15/05/2026`. */
export function fmtDiaLongo(day: string): string {
  const { y, m, d, weekday } = parts(day);
  return `${DIAS[weekday]}, ${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
}

/** Ticks inteiros e redondos: contagem nao tem meio usuario, entao o passo e 1, 2 ou 5. */
function ticks(max: number, count = 4): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / magnitude;
  const step = Math.max(1, (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * magnitude);
  const out = [];
  for (let v = 0; v < max + step; v += step) out.push(v);
  return out;
}

export interface DailyChartProps {
  days: string[];
  values: number[];
  kind: "line" | "bar";
  /** O que o numero conta, no plural: aparece no tooltip ("1.234 usuários"). */
  unit: string;
  title: string;
}

export function DailyChart({ days, values, kind, unit, title }: DailyChartProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [active, setActive] = useState<number | null>(null);

  useLayoutEffect(() => {
    const node = boxRef.current;
    if (!node) return;
    setWidth(node.clientWidth);
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const n = values.length;
  const max = Math.max(...values, 0);
  const yTicks = ticks(max);
  const top = yTicks[yTicks.length - 1];
  // A margem esquerda cresce com o rotulo mais largo do eixo: "80 mil" nao cabe onde
  // "30" cabe. ~6,8px por caractere no mono de 11px, mais o respiro ate o grid.
  const PL = 14 + 6.8 * Math.max(...yTicks.map((t) => compact(t).length));
  const plotW = Math.max(0, width - PL - PR);
  const band = n ? plotW / n : 0;
  // Linha: um ponto no centro de cada dia, para casar com as colunas do grafico vizinho.
  const x = (i: number) => PL + band * (i + 0.5);
  const y = (v: number) => PT + (H - PT - PB) * (1 - v / top);
  const base = y(0);

  // Rotulo de data a cada tantos dias quanto couberem com ~72px de folga.
  const every = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotW / 72))));
  const xTicks = days.map((_, i) => i).filter((i) => i % every === 0);

  const pick = (clientX: number) => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box || !n || !band) return;
    const i = Math.floor((clientX - box.left - PL) / band);
    setActive(Math.max(0, Math.min(n - 1, i)));
  };

  const onKey = (event: React.KeyboardEvent) => {
    if (!n) return;
    const step = { ArrowLeft: -1, ArrowRight: 1, Home: -n, End: n }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    setActive((current) => Math.max(0, Math.min(n - 1, (current ?? n - 1) + step)));
  };

  const last = n - 1;
  const total = kind === "line" ? values[last] ?? 0 : values.reduce((a, b) => a + b, 0);
  const label = n
    ? `${title}: ${intBR(total)} ${unit} ${kind === "line" ? "ao fim de" : "em"} ${n} dias, ` +
      `de ${fmtDiaLongo(days[0])} a ${fmtDiaLongo(days[last])}` +
      (kind === "bar" ? `; máximo de ${intBR(max)} num dia` : "")
    : `${title}: sem dados`;

  const line = values.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join("");
  const area = n ? `${line}L${x(last).toFixed(1)},${base}L${x(0).toFixed(1)},${base}Z` : "";
  // Coluna: no maximo 24px, e sempre 2px de ar entre vizinhas.
  const barW = Math.max(1, Math.min(24, band - 2));
  const radius = Math.min(4, barW / 2);

  const column = (i: number, v: number) => {
    const left = x(i) - barW / 2, right = left + barW, height = base - y(v);
    const r = Math.min(radius, height);
    return `M${left},${base}V${base - height + r}Q${left},${base - height} ${left + r},` +
      `${base - height}H${right - r}Q${right},${base - height} ${right},${base - height + r}` +
      `V${base}Z`;
  };

  // O tooltip fica ao lado do dia, nunca em cima dele: do lado direito na primeira
  // metade do grafico, do esquerdo na segunda.
  const tipOnLeft = active !== null && x(active) > width / 2;

  return (
    <div ref={boxRef} style={{ position: "relative", width: "100%" }}>
      {width > 0 && (
        <svg width={width} height={H} role="img" aria-label={label} tabIndex={0}
             onPointerMove={(event) => pick(event.clientX)}
             onPointerLeave={() => setActive(null)}
             onKeyDown={onKey}
             onBlur={() => setActive(null)}
             style={{ display: "block", touchAction: "pan-y", outlineOffset: 2 }}>
          {yTicks.map((tick) => (
            <g key={tick}>
              <line x1={PL} x2={width - PR} y1={y(tick)} y2={y(tick)}
                    stroke={tick ? "var(--grid)" : "var(--axis)"} strokeWidth="1" />
              <text x={PL - 8} y={y(tick)} dy="0.32em" textAnchor="end" fontSize="11"
                    fill="var(--ink-3)" fontFamily="var(--font-mono)">
                {compact(tick)}
              </text>
            </g>
          ))}

          {xTicks.map((i) => (
            <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="11"
                  fill="var(--ink-3)" fontFamily="var(--font-mono)">
              {fmtDia(days[i])}
            </text>
          ))}

          {active !== null && kind === "bar" && (
            <rect x={x(active) - band / 2} y={PT} width={band} height={base - PT}
                  fill="var(--surface-2)" />
          )}

          {kind === "bar" && values.map((v, i) => v > 0 && (
            <path key={i} d={column(i, v)} fill="var(--accent)"
                  opacity={active === null || active === i ? 1 : 0.55} />
          ))}

          {kind === "line" && n > 0 && (
            <>
              <path d={area} fill="var(--accent)" opacity="0.1" />
              <path d={line} fill="none" stroke="var(--accent)" strokeWidth="2"
                    strokeLinejoin="round" strokeLinecap="round" />
              {active === null && (
                <>
                  <circle cx={x(last)} cy={y(values[last])} r="4" fill="var(--accent)"
                          stroke="var(--surface)" strokeWidth="2" />
                  <text x={x(last) - 8} y={y(values[last]) - 9} textAnchor="end"
                        fontSize="11.5" fontWeight="600" fill="var(--ink)"
                        fontFamily="var(--font-mono)">
                    {intBR(values[last])}
                  </text>
                </>
              )}
            </>
          )}

          {active !== null && kind === "line" && (
            <>
              <line x1={x(active)} x2={x(active)} y1={PT} y2={base}
                    stroke="var(--axis)" strokeWidth="1" />
              <circle cx={x(active)} cy={y(values[active])} r="4" fill="var(--accent)"
                      stroke="var(--surface)" strokeWidth="2" />
            </>
          )}
        </svg>
      )}

      {active !== null && (
        <div role="status" aria-live="polite"
             style={{ position: "absolute", top: PT, left: x(active) + (tipOnLeft ? -12 : 12),
                      transform: tipOnLeft ? "translateX(-100%)" : undefined,
                      pointerEvents: "none",
                      background: "var(--surface)", border: "1px solid var(--border)",
                      borderRadius: "var(--radius-control)", boxShadow: "var(--shadow)",
                      padding: "5px 9px", whiteSpace: "nowrap" }}>
          <div style={{ fontWeight: 600, fontFamily: "var(--font-mono)", fontSize: 13 }}>
            {intBR(values[active])} <span style={{ fontWeight: 400 }}>{unit}</span>
          </div>
          <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{fmtDiaLongo(days[active])}</div>
        </div>
      )}
    </div>
  );
}
